import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';

import { AnimationBase } from '../animation-base';

@Component({
  selector: 'mdw-fade',
  templateUrl: './fade.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./fade.scss'],
})
export class MdwFade extends AnimationBase {
  private readonly destroyRef = inject(DestroyRef);

  public readonly isVisible = input.required<boolean>();
  public readonly deferEnter = input<boolean>(false);
  public readonly verticalOffset = input<number>(0);
  public readonly skipEnterAnimation = input<boolean>(false);
  public readonly skipLeaveAnimation = input<boolean>(false);
  public readonly animateInit = input<boolean>(false);

  // The leave is self-managed (fade-out class now, unmount after the duration)
  // instead of `animate.leave`: the framework's leave bookkeeping is keyed by
  // template node, so while one mdw-fade's element was animating out, ANY other
  // mdw-fade instance mounting anywhere would remove it from the DOM
  // immediately (e.g. the message hover menu when the cursor moves to the next
  // message, whose own menu mounts in the same coalesced change detection).
  private readonly mounted = signal(false);

  /**
   * True from the moment the fade content mounts until the leave animation
   * finishes. Projected content that must not disappear mid-leave can gate on
   * this instead of the state driving `isVisible`.
   */
  public readonly isMounted = this.mounted.asReadonly();

  // The enter is class-driven (not `animate.enter`): `animate.enter` fires
  // only on DOM insertion, but a leave keeps the node mounted here, so a
  // hide -> show flip during the leave window would never re-trigger it and
  // the element would snap from mid-fade to opaque. Re-adding the class
  // restarts the fade-in instead.
  protected readonly isEntering = computed(() => {
    return this.isVisible() && !this.skipEnterAnimation();
  });

  protected readonly isLeaving = computed(() => {
    return !this.isVisible() && !this.skipLeaveAnimation();
  });

  protected readonly ignoreLeave = signal(false);

  private startTimeout?: ReturnType<typeof setTimeout>;
  private endTimeout?: ReturnType<typeof setTimeout>;
  private destroyed = false;

  public constructor() {
    super();

    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.clearStartTimeout();
      this.clearEndTimeout();
    });

    effect(() => {
      this.clearStartTimeout();
      this.clearEndTimeout();

      const isVisible = this.isVisible();
      const shouldAnimate = this.shouldAnimate() || !this.animateInit();

      if (isVisible) {
        untracked(() => this.mounted.set(true));
      }

      if (!shouldAnimate) {
        if (!isVisible) {
          untracked(() => this.mounted.set(false));
        }
        return;
      }

      let startTimeout = 0;
      let endTimeout = 0;

      // The stylesheet forces animation-duration to 0 under reduced motion,
      // so the timers must collapse with it — otherwise a leaving element
      // that is already visually gone stays mounted (and a shown one delays
      // its start/end events) for the full duration.
      const reducedMotion =
        typeof window !== 'undefined' &&
        !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      if (isVisible) {
        const delay = this.deferEnter() ? this.animationDuration() : this.animationEnterDelay();
        startTimeout = reducedMotion ? 0 : delay * 1000;
        untracked(() => this.ignoreLeave.set(true));
      }

      const skipAnimation = isVisible ? this.skipEnterAnimation() : this.skipLeaveAnimation();
      const duration = reducedMotion || skipAnimation ? 0 : this.animationDuration();
      endTimeout = startTimeout + duration * 1000;

      if (startTimeout > 0) {
        this.startTimeout = setTimeout(() => {
          this.startTimeout = undefined;

          if (this.destroyed) {
            return;
          }

          this.handleAnimationStart.emit();
        }, startTimeout);
      } else if (!this.destroyed) {
        this.handleAnimationStart.emit();
      }

      const completeAnimation = () => {
        if (this.destroyed) {
          return;
        }

        this.endTimeout = undefined;
        untracked(() => {
          this.ignoreLeave.set(false);
          if (!isVisible) {
            this.mounted.set(false);
          }
        });
        this.handleAnimationEnd.emit();
      };

      if (endTimeout > 0) {
        this.endTimeout = setTimeout(completeAnimation, endTimeout);
      } else {
        completeAnimation();
      }
    });
  }

  private clearStartTimeout(): void {
    if (this.startTimeout) {
      clearTimeout(this.startTimeout);
      this.startTimeout = undefined;
    }
  }

  private clearEndTimeout(): void {
    if (this.endTimeout) {
      clearTimeout(this.endTimeout);
      this.endTimeout = undefined;
    }
  }
}
